import { z } from 'zod';
import { IdSchema, IsoDateSchema, IsoDateTimeSchema } from './common.js';
import { QuestionnaireAnswerSchema, QuestionnaireQuestionSchema } from './tdd.js';
/**
 * Training projects (projectType 'training') have their own pipeline in place of PRD -> TDD:
 *
 *   sources -> (follow-up questions) -> sessions list -> one outline per session -> slide deck
 *
 * Every step is generated by Gemini and confirmed by a lead (training.manage). Slide decks are
 * structured — positioned elements on a fixed SLIDE_WIDTH x SLIDE_HEIGHT canvas, see
 * schemas/slides.ts — so the in-app editor, the PDF export and the PPTX export all work from the
 * same data.
 */
/** How many source files one training project may hold. Every generation step reads all of them. */
export const TRAINING_SOURCE_MAX_FILES = 15;
/** Upper bound on a sessions list, whatever min/max the user asks for. */
export const TRAINING_MAX_SESSIONS = 40;
/** Upper bound on one session's slide count. */
export const TRAINING_MAX_SLIDES = 40;
// ---- Fonts & design ----
/** The fonts the design picker offers — all on Google Fonts, so the web app and the PDF export
 * render them identically. PPTX can't embed fonts: these need to be installed on the machine
 * that opens the file, or PowerPoint substitutes its default. */
export const TRAINING_FONTS = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Nunito',
    'Raleway',
    'Work Sans',
    'DM Sans',
    'Source Sans 3',
    'IBM Plex Sans',
    'Space Grotesk',
    'Merriweather',
    'Lora',
    'Playfair Display',
    'IBM Plex Serif',
    'Fira Code',
    // The style presets' fonts (see slides/presets.ts) — each has 400 and 700 weights.
    'Archivo',
    'Manrope',
    'Syne',
    'Outfit',
    'Plus Jakarta Sans',
    'Fraunces',
    'Cormorant',
    'Cormorant Garamond',
    'Source Serif 4',
    'JetBrains Mono',
    'Space Mono',
];
export const TrainingFontSchema = z.enum(TRAINING_FONTS);
/** Fonts that fall back to a generic serif or monospace rather than sans-serif. */
export const SERIF_FONTS = [
    'Merriweather',
    'Lora',
    'Playfair Display',
    'IBM Plex Serif',
    'Fraunces',
    'Cormorant',
    'Cormorant Garamond',
    'Source Serif 4',
];
export const MONO_FONTS = ['Fira Code', 'JetBrains Mono', 'Space Mono'];
/**
 * The decoration generated slides get, drawn as ordinary shapes so it stays editable and exports
 * to PPTX. minimal is none at all; the others each follow one look:
 * - signal: a solid accent card as the focal point of the title slide, large section numerals
 * - swiss: rules and a solid accent square, sections on the accent color
 * - editorial: hairline rules, an outlined circle and dot, display type set in regular weight
 * - soft: rounded cards and a column of pills
 */
export const SLIDE_STYLES = ['minimal', 'signal', 'swiss', 'editorial', 'soft'];
export const SlideStyleNameSchema = z.enum(SLIDE_STYLES);
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a #rrggbb color');
/** The structured design settings. `otherDetails` is free text for anything the fields don't
 * cover (layout preferences, imagery style, tone of visuals...) and is passed to Gemini as-is. */
export const TrainingDesignSchema = z.object({
    headingFont: TrainingFontSchema,
    bodyFont: TrainingFontSchema,
    primaryColor: HexColorSchema,
    accentColor: HexColorSchema,
    backgroundColor: HexColorSchema,
    textColor: HexColorSchema,
    /** Decks and designs saved before styles existed have none, which means minimal. */
    style: SlideStyleNameSchema.default('minimal'),
    otherDetails: z.string().max(2000).default(''),
});
export const DEFAULT_TRAINING_DESIGN = {
    headingFont: 'Montserrat',
    bodyFont: 'Inter',
    primaryColor: '#1f2a44',
    accentColor: '#c8a24a',
    backgroundColor: '#ffffff',
    textColor: '#1f2328',
    style: 'minimal',
    otherDetails: '',
};
/**
 * How much goes on a slide. focused is speaker-led — one idea per slide in large type, the
 * speaker notes carry the detail; detailed is for decks read without a trainer. Content that
 * doesn't fit the density gets more slides, never smaller type.
 */
export const SLIDE_DENSITIES = ['focused', 'balanced', 'detailed'];
export const SlideDensitySchema = z.enum(SLIDE_DENSITIES);
/** Generation instructions (style of the training: technical, funny, serious...) plus the design.
 * The org holds the defaults, each training project its own copy, and each generation run may
 * override either for that run. */
export const TrainingInstructionsSchema = z.object({
    generationInstructions: z.string().max(4000).default(''),
    density: SlideDensitySchema.default('balanced'),
    design: TrainingDesignSchema,
});
export const DEFAULT_TRAINING_INSTRUCTIONS = {
    generationInstructions: '',
    density: 'balanced',
    design: DEFAULT_TRAINING_DESIGN,
};
/** orgs/{orgId}/settings/trainingDefaults — copied into each new training project. */
export const TrainingDefaultsSchema = TrainingInstructionsSchema.extend({
    updatedAt: IsoDateTimeSchema.optional(),
    updatedByUid: IdSchema.optional(),
});
export const UpdateTrainingDefaultsInputSchema = TrainingInstructionsSchema;
/** Sent with any generation request: overrides the project's instructions for that run only,
 * unless `saveAsProjectDefault` is set, in which case they also replace the project's copy. */
export const InstructionsOverrideSchema = z.object({
    instructions: TrainingInstructionsSchema.optional(),
    saveAsProjectDefault: z.boolean().default(false),
});
// ---- Stage ----
/** Where a training project stands — derived from its state and sessions on every write (see
 * refreshTrainingStage in the API) and shown on the dashboard. */
export const TRAINING_STAGES = [
    'sources',
    'questions',
    'sessions',
    'outlines',
    'slides',
    'ready',
];
export const TrainingStageSchema = z.enum(TRAINING_STAGES);
export const TRAINING_STAGE_LABEL = {
    sources: 'Adding sources',
    questions: 'Answering questions',
    sessions: 'Planning sessions',
    outlines: 'Outlining sessions',
    slides: 'Building slides',
    ready: 'Slides ready',
};
// ---- Sources ----
/** projects/{id}/trainingSources/{id} — one uploaded context file. */
export const TrainingSourceSchema = z.object({
    id: IdSchema,
    filename: z.string().min(1).max(300),
    storagePath: z.string().min(1).max(1024),
    mimeType: z.string(),
    addedAt: IsoDateTimeSchema,
    addedByUid: IdSchema,
});
export const AddTrainingSourcesInputSchema = z.object({
    sources: z
        .array(z.object({ filename: z.string().min(1).max(300), storagePath: z.string().min(1).max(1024) }))
        .min(1)
        .max(TRAINING_SOURCE_MAX_FILES),
});
// ---- Analysis & project-level training state ----
/** What Gemini reads off the sources before planning: who the training is for, in what language,
 * how complex the topic is, and any follow-up questions it needs answered (possibly none). */
export const TrainingAnalysisResultSchema = z.object({
    topic: z.string().min(1).max(300),
    audience: z.string().min(1).max(500),
    language: z.string().min(1).max(100),
    complexity: z.enum(['introductory', 'intermediate', 'advanced']),
    suggestedMinSessions: z.number().int().min(1).max(TRAINING_MAX_SESSIONS),
    suggestedMaxSessions: z.number().int().min(1).max(TRAINING_MAX_SESSIONS),
    rationale: z.string().max(1000),
    questions: z.array(QuestionnaireQuestionSchema).max(8),
});
/** projects/{id}/training/state — the project-wide training settings and progress. */
export const TrainingStateSchema = z.object({
    stage: TrainingStageSchema.default('sources'),
    topic: z.string().max(300).nullable().default(null),
    audience: z.string().max(500).nullable().default(null),
    language: z.string().max(100).nullable().default(null),
    complexity: z.enum(['introductory', 'intermediate', 'advanced']).nullable().default(null),
    /** Gemini's recommendation from the analysis, shown next to the user's own min/max. */
    suggestedMinSessions: z.number().int().positive().nullable().default(null),
    suggestedMaxSessions: z.number().int().positive().nullable().default(null),
    suggestionRationale: z.string().max(1000).nullable().default(null),
    /** Both optional — unset means "let Gemini decide from the topic's complexity". */
    minSessions: z.number().int().positive().nullable().default(null),
    maxSessions: z.number().int().positive().nullable().default(null),
    questions: z.array(QuestionnaireQuestionSchema).default([]),
    answers: z.array(QuestionnaireAnswerSchema).default([]),
    questionsAnsweredAt: IsoDateTimeSchema.nullable().default(null),
    analyzedAt: IsoDateTimeSchema.nullable().default(null),
    sessionsConfirmedAt: IsoDateTimeSchema.nullable().default(null),
    instructions: TrainingInstructionsSchema,
    updatedAt: IsoDateTimeSchema.optional(),
});
const SessionCountSchema = z.number().int().min(1).max(TRAINING_MAX_SESSIONS);
export const UpdateTrainingSettingsInputSchema = z
    .object({
    audience: z.string().max(500).nullable().optional(),
    language: z.string().max(100).nullable().optional(),
    minSessions: SessionCountSchema.nullable().optional(),
    maxSessions: SessionCountSchema.nullable().optional(),
    instructions: TrainingInstructionsSchema.optional(),
})
    .refine((i) => !i.minSessions || !i.maxSessions || i.minSessions <= i.maxSessions, {
    message: 'minSessions must not exceed maxSessions',
    path: ['minSessions'],
});
export const SubmitTrainingAnswersInputSchema = z.object({
    answers: z.array(QuestionnaireAnswerSchema),
});
// ---- Sessions ----
/** The plan for one session: what the sessions-list step generates and the user edits by hand. */
export const SessionPlanSchema = z.object({
    title: z.string().min(1).max(200),
    summary: z.string().min(1).max(2000),
    learningObjectives: z.array(z.string().min(1).max(300)).min(1).max(10),
    durationMinutes: z
        .number()
        .int()
        .min(5)
        .max(8 * 60),
    /** Suggested by Gemini, editable by the user — the outline step generates exactly this many. */
    slideCount: z.number().int().min(1).max(TRAINING_MAX_SLIDES),
});
export const GeneratedSessionsSchema = z.object({
    sessions: z.array(SessionPlanSchema).min(1).max(TRAINING_MAX_SESSIONS),
});
/** The illustration, image or diagram a slide calls for, described in words at the outline
 * stage. At the slides stage a diagram becomes an SVG and the other two become image
 * placeholders the user fills from the editor. */
export const OUTLINE_VISUAL_KINDS = ['illustration', 'image', 'diagram'];
export const OutlineVisualSchema = z.object({
    kind: z.enum(OUTLINE_VISUAL_KINDS),
    description: z.string().min(1).max(1000),
});
export const OutlineSlideSchema = z.object({
    headline: z.string().min(1).max(200),
    description: z.string().min(1).max(1500),
    visual: OutlineVisualSchema.nullable().default(null),
});
export const SessionOutlineSchema = z.object({
    title: z.string().min(1).max(200),
    slides: z.array(OutlineSlideSchema).min(1).max(TRAINING_MAX_SLIDES),
});
export const TRAINING_SESSION_STATUSES = [
    'planned',
    'outline_draft',
    'outline_confirmed',
    'generating',
    'ready',
];
export const TrainingSessionStatusSchema = z.enum(TRAINING_SESSION_STATUSES);
export const TRAINING_SESSION_STATUS_LABEL = {
    planned: 'Planned',
    outline_draft: 'Outline draft',
    outline_confirmed: 'Outline confirmed',
    generating: 'Generating slides',
    ready: 'Slides ready',
};
// ---- Session prep tasks ----
/** The tasks every session gets, once, the first time its slides are generated — see
 * addSessionPrepTasks in routes/training.ts. */
export const SESSION_PREP_TASK_KINDS = [
    'facilitator_guide',
    'participant_guide',
    'exercises',
];
export const SessionPrepTaskKindSchema = z.enum(SESSION_PREP_TASK_KINDS);
export const SESSION_PREP_TASK_TITLE = {
    facilitator_guide: 'Prepare facilitator guide',
    participant_guide: 'Prepare participant guide',
    exercises: 'Prepare exercises and mock data',
};
export const SESSION_PREP_TASK_LABEL = {
    facilitator_guide: 'facilitator-guide',
    participant_guide: 'participant-guide',
    exercises: 'exercises',
};
/** The board label naming a session — kept in step with the session's position. */
export function sessionLabel(order) {
    return `session-${order + 1}`;
}
/** A prep task's title — kept in step with the session's position and title unless someone has
 * edited it. */
export function sessionPrepTaskTitle(kind, session) {
    return `Session ${session.order + 1}: ${SESSION_PREP_TASK_TITLE[kind]} — ${session.title}`.slice(0, 300);
}
const PrepTaskContentSchema = z.object({
    description: z.string().min(1).max(5000),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).min(1).max(8),
});
/** What the training.prep_tasks prompt returns: a description and checklist per prep task. */
export const GeneratedPrepTasksSchema = z.object({
    facilitator_guide: PrepTaskContentSchema,
    participant_guide: PrepTaskContentSchema,
    exercises: PrepTaskContentSchema,
});
/** projects/{id}/trainingSessions/{id} */
export const TrainingSessionSchema = SessionPlanSchema.extend({
    id: IdSchema,
    order: z.number().int().nonnegative(),
    /** Set by the user — drives the sessions timeline. */
    date: IsoDateSchema.nullable().default(null),
    status: TrainingSessionStatusSchema.default('planned'),
    outline: SessionOutlineSchema.nullable().default(null),
    outlineConfirmedAt: IsoDateTimeSchema.nullable().default(null),
    currentDeckVersionId: IdSchema.nullable().default(null),
    currentDeckVersionNumber: z.number().int().nonnegative().default(0),
    /** Last slide-generation failure, cleared on the next attempt. */
    slidesError: z.string().max(2000).nullable().default(null),
    /** Set when sources added after this session was planned may change it — see the
     * training_impact job. Cleared by regenerating or dismissing. */
    stale: z
        .object({ reason: z.string().max(1000), flaggedAt: IsoDateTimeSchema })
        .nullable()
        .default(null),
    deliverableId: IdSchema.nullable().default(null),
    /** The session's prep tasks by kind, set once when they're created; null until then. Sessions
     * written before this field existed have none in Firestore — read them with `?? null`. */
    prepTaskIds: z.record(SessionPrepTaskKindSchema, IdSchema).nullable().default(null),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
/** PUT .../training/sessions — the whole list, in order, as edited by hand. Rows with an `id`
 * update that session (its outline and slides are kept), rows without one are new sessions, and
 * sessions missing from the list are deleted along with their slides. */
export const SaveSessionsInputSchema = z.object({
    sessions: z
        .array(SessionPlanSchema.extend({
        id: IdSchema.optional(),
        date: IsoDateSchema.nullable().default(null),
    }))
        .min(1)
        .max(TRAINING_MAX_SESSIONS),
});
export const GenerateSessionsInputSchema = InstructionsOverrideSchema.extend({
    /** "Regenerate with feedback": what to change about the current list. */
    feedback: z.string().max(4000).optional(),
});
export const GenerateOutlineInputSchema = InstructionsOverrideSchema.extend({
    feedback: z.string().max(4000).optional(),
});
export const SaveOutlineInputSchema = z.object({ outline: SessionOutlineSchema });
/** Confirming an outline starts slide generation straight away, with these overrides. */
export const ConfirmOutlineInputSchema = InstructionsOverrideSchema;
export const GenerateSlidesInputSchema = InstructionsOverrideSchema.extend({
    feedback: z.string().max(4000).optional(),
});
/** What the training_impact job returns: which sessions new sources affect, and why. */
export const TrainingImpactResultSchema = z.object({
    affected: z.array(z.object({ sessionId: IdSchema, reason: z.string().min(1).max(1000) })),
});
// ---- Export ----
export const TRAINING_EXPORT_FORMATS = ['pdf', 'pptx'];
export const TrainingExportFormatSchema = z.enum(TRAINING_EXPORT_FORMATS);
export const RequestTrainingExportInputSchema = z.object({
    format: TrainingExportFormatSchema,
    /** One session, or every session with slides when omitted. */
    sessionId: IdSchema.optional(),
});
//# sourceMappingURL=training.js.map